import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";
import { useAuthStore } from "./stores/auth";

// Lazy-loaded views keep the initial bundle small.
const LandingView = () => import("./views/LandingView.vue");
const LoginView = () => import("./views/LoginView.vue");
const DashboardView = () => import("./views/DashboardView.vue");
const DepartmentsView = () => import("./views/DepartmentsView.vue");
const UsersView = () => import("./views/UsersView.vue");
const ProvidersView = () => import("./views/ProvidersView.vue");
const AgentsView = () => import("./views/AgentsView.vue");
const KnowledgeView = () => import("./views/KnowledgeView.vue");
const ApiKeysView = () => import("./views/ApiKeysView.vue");
const ConnectionsView = () => import("./views/ConnectionsView.vue");
const WorkflowLabView = () => import("./views/WorkflowLabView.vue");
const ChatView = () => import("./views/ChatView.vue");
const UsageView = () => import("./views/UsageView.vue");

/*
 * meta.surface names the SURFACE a route belongs to; who may reach it is
 * decided by `backend/app/services/surface_policy.py` and arrives on the
 * auth payload as `user.surfaces`. meta.title/subtitle feed the AppShell
 * topbar.
 *
 * THIS REPLACES THE OLD `meta.roles` ARRAYS ON PURPOSE. They were a
 * second copy of the server's mapping, written in TypeScript, and the
 * decision they encoded is explicitly provisional — "super admin only, I
 * will let you know about other mapping later". A copy that has to be
 * edited in two languages every time the mapping moves is the exact
 * failure `app/services/scoping.py` exists to prevent. `roles:
 * ["super_admin"]` on /backend-console is now `surface:
 * "backend_console"`, and the answer comes from the same table the API
 * refuses requests with.
 *
 * The guard below is legibility — it stops a member from staring at a
 * page of 403s. It is not the access control; the server is.
 */
const routes: RouteRecordRaw[] = [
  { path: "/", name: "landing", component: LandingView, meta: { public: true } },
  { path: "/login", name: "login", component: LoginView, meta: { public: true } },
  {
    path: "/dashboard",
    name: "dashboard",
    component: DashboardView,
    // NOT "Platform-wide" any more. /usage/summary is RBAC-scoped: a
    // dept_admin sees their department, an org_admin their organization,
    // and only a super_admin sees the platform. The old subtitle was the
    // caption on a cross-tenant leak — it told an ELFA dept_admin that
    // Wavetec's spend was "platform-wide", which is exactly how nobody
    // noticed. The scope the numbers actually cover is on the page.
    meta: { surface: "dashboard", title: "Dashboard", subtitle: "Usage, spend and health for everything you can see" },
  },
  {
    path: "/usage",
    name: "usage",
    component: UsageView,
    meta: { surface: "usage", title: "Usage & Costs", subtitle: "Filterable usage explorer with CSV export" },
  },
  {
    path: "/traces",
    name: "tracing",
    component: () => import("./views/TracingView.vue"),
    meta: { surface: "traces",
      title: "Tracing",
      subtitle: "Per-turn pipeline traces across all conversations",
    },
  },
  {
    path: "/approvals",
    name: "approvals",
    component: () => import("./views/ApprovalsView.vue"),
    meta: { surface: "approvals",
      title: "Approvals",
      subtitle: "Human-in-the-loop gates waiting on you",
    },
  },
  {
    path: "/organizations",
    name: "organizations",
    component: () => import("./views/OrganizationsView.vue"),
    meta: { surface: "organizations",
      title: "Organizations",
      subtitle: "Tenants, their email domains, members and departments",
      // The outer tenancy boundary is a super_admin / org_admin surface;
      // a dept_admin administers a department INSIDE one org and has no
      // business here (the API agrees — every write below /organizations
      // goes through can_administer_organization).
    },
  },
  {
    path: "/departments",
    name: "departments",
    component: DepartmentsView,
    meta: { surface: "departments",
      title: "Departments",
      // M2 — "provider keys" left this sentence with the assignment
      // dialog: a department has not had a key of its own since M1
      // collapsed the chain to `organization -> environment`.
      subtitle: "Org units with budgets and agent visibility",
    },
  },
  {
    path: "/users",
    name: "users",
    component: UsersView,
    meta: { surface: "users",
      title: "Users",
      subtitle: "People, roles and access",
    },
  },
  {
    path: "/providers",
    name: "providers",
    component: ProvidersView,
    meta: { surface: "providers",
      title: "Platform Catalogue",
      subtitle:
        "The shared LLM providers every tenant may attach a key to, and the models each one offers",
    },
  },
  {
    path: "/backend-console",
    name: "backend-console",
    component: () => import("./views/BackendConsoleView.vue"),
    meta: { surface: "backend_console",
      title: "Backend Console",
      subtitle:
        "What one worker process is doing right now, and how to make it re-read itself",
      // PLATFORM INTERNALS, not tenant configuration — there is no
      // organization this could be scoped to, so org_admin is NOT listed
      // here even though it is listed almost everywhere else. Matches
      // scoping.can_administer_platform on the server, which refuses
      // everybody else independently of this guard.
    },
  },
  {
    path: "/role-mapping",
    name: "role-mapping",
    component: () => import("./views/RoleMappingView.vue"),
    meta: { surface: "role_mapping",
      title: "Role Mapping",
      subtitle:
        "Which roles may reach which surfaces — and what each surface still requires underneath",
      // PINNED super_admin on the server (surface_policy.PINNED_SURFACES)
      // and NOT editable from this page in either direction: closing it
      // would lock the platform owner out of the only control that could
      // re-open it, and widening it would hand somebody the ability to
      // grant themselves everything else. The guard is in the policy, not
      // in this guard — a curl gets the same 400.
    },
  },
  {
    path: "/agents",
    name: "agents",
    component: AgentsView,
    meta: { surface: "agents",
      title: "AI Agents",
      subtitle: "Master → agent → sub-agent hierarchy, prompts and routing",
    },
  },
  {
    path: "/knowledge",
    name: "knowledge",
    component: KnowledgeView,
    meta: { surface: "knowledge",
      title: "Knowledgebase",
      subtitle: "Collections powering agent retrieval (RAG)",
    },
  },
  {
    path: "/tools",
    name: "tools",
    component: () => import("./views/ToolsView.vue"),
    meta: { surface: "tools",
      title: "Tools",
      subtitle: "REST, OpenAPI and MCP tools across all agents",
    },
  },
  {
    path: "/workflows",
    name: "workflows",
    component: () => import("./views/WorkflowsView.vue"),
    meta: { surface: "workflows_run",
      title: "Workflows",
      subtitle: "Visual multi-step agent orchestration",
      // Members get a run-only view of workflows visible to their
      // department; building/editing stays admin-gated in the view.
    },
  },
  {
    path: "/workflow-analytics",
    name: "workflow-analytics",
    component: () => import("./views/WorkflowAnalyticsView.vue"),
    meta: { surface: "workflow_analytics",
      title: "Workflow Analytics",
      subtitle: "Gate timings, decisions, outcomes and form answers per workflow",
      // "Anyone who can already see the workflow" — which starts at
      // dept_admin, because the endpoint is require_role(ROLE_DEPT_ADMIN)
      // and a member is refused there. org_admin is listed for the same
      // reason it is everywhere else: the backend ladder says an
      // org_admin satisfies every dept_admin check, so omitting it would
      // lock them out of a page the API serves them, org-scoped. Which
      // WORKFLOWS a dept_admin then sees is scoping.is_workflow_visible,
      // decided per request on the server — never here.
    },
  },
  {
    // THE PATH AND THE ROUTE NAME ARE UNCHANGED, THE HEADING IS NOT.
    //
    // CA3 gave this page a second tab (Chat: WhatsApp and gateway
    // conversations), so "Voice Analytics" over it is no longer true and
    // the title is channel-neutral. The PATH stays `/voice-analytics`
    // anyway, for two reasons that outrank tidiness:
    //
    //   * `surface_policy.PAGE_PATHS["voice_analytics"]` is that exact
    //     string on the server, and the Role Mapping page renders it as
    //     the link for the surface. Changing it here would mean changing
    //     it there too — one decision in two languages, which is the
    //     failure `meta.surface` replaced `meta.roles` to end.
    //   * every bookmark, runbook and notification `link` already written
    //     points at it. A URL that moves because a heading moved teaches
    //     people that links out of this app do not last.
    //
    // The tab itself lives in the query (`?tab=voice` / `?tab=chat`), so
    // a link can still name either half.
    path: "/voice-analytics",
    name: "voice-analytics",
    component: () => import("./views/ChannelAnalyticsView.vue"),
    meta: { surface: "voice_analytics",
      title: "Channel Analytics",
      subtitle:
        "Calls and WhatsApp conversations — volume, how each ended, drop-out and spend",
      // ONE SURFACE FOR BOTH TABS, deliberately. `GET /chat/analytics`
      // sits behind `voice_analytics` on the server as well (see the
      // module docstring of backend/app/api/chat_analytics.py): granting
      // somebody this page and then having its second tab 403 would be a
      // broken screen, and a surface of its own would put a second row on
      // Role Mapping for something that is not a second page.
      //
      // Same reasoning as its twin above: both endpoints are
      // require_role(ROLE_DEPT_ADMIN), so dept_admin is the floor and
      // org_admin satisfies it through the backend ladder. Which CALLS
      // each of them then sees is scoping.run_scope_conditions and which
      // CONVERSATIONS is the channel ladder — both decided per request on
      // the server, never here. The surface is
      // SUPER_ONLY by default and, unlike backend_console, DELEGATABLE:
      // a super_admin can hand it to a department role on Role Mapping.
    },
  },
  {
    path: "/forms",
    name: "forms",
    component: () => import("./views/FormsView.vue"),
    meta: { surface: "forms",
      title: "Forms",
      subtitle: "Structured documents with section-level access and audit history",
      // All roles: admins build, members fill published forms (F1).
    },
  },
  {
    path: "/form-instances",
    name: "form-instances",
    component: () => import("./views/FormInstancesView.vue"),
    // A SURFACE OF ITS OWN. ``forms`` is the builder; this is the case
    // list, and the people who need it are the support desk rather than
    // the people who design documents. Sharing the surface would mean
    // granting the form editor to read a case.
    meta: { surface: "form_instances",
      title: "Form Instances",
      subtitle: "Every case raised against a form — filter by channel, date, customer or centre",
    },
  },
  {
    path: "/ask-ai",
    name: "ask-ai",
    component: () => import("./views/AskAiView.vue"),
    meta: { surface: "ask_ai",
      title: "Ask AI",
      subtitle: "Upload a paper form — the model proposes the schema, you confirm it",
      // Same floor as the API (require_role(ROLE_DEPT_ADMIN)): offering a
      // member the link would be a lie, the endpoint 403s them.
    },
  },
  {
    path: "/scheduler",
    name: "scheduler",
    component: () => import("./views/SchedulerView.vue"),
    meta: { surface: "scheduler",
      title: "Scheduler",
      subtitle: "Run agents on a schedule and review results",
    },
  },
  {
    path: "/api-keys",
    name: "apikeys",
    component: ApiKeysView,
    meta: { surface: "api_keys",
      title: "API Keys",
      subtitle: "Public Agent API bearer keys with expiry",
    },
  },
  {
    path: "/workflow-lab",
    name: "workflowlab",
    component: WorkflowLabView,
    meta: {
      surface: "workflow_lab",
      title: "Workflow Lab",
      subtitle: "Hands-on exercises — build a graph, break it, fix it",
    },
  },
  {
    path: "/connections",
    name: "connections",
    component: ConnectionsView,
    meta: {
      surface: "service_connections",
      title: "Connections",
      subtitle: "How to reach an external system — base URL, account, credential",
    },
  },
  {
    path: "/channels",
    name: "channels",
    component: () => import("./views/ChannelsView.vue"),
    meta: { surface: "channels",
      title: "Channels",
      subtitle: "Connect external messaging apps — WhatsApp Business Cloud API — to your agents",
    },
  },
  // W1 — the PUBLIC click-to-call page. `public: true` so the guard lets
  // it through without a session: a link shared on WhatsApp reaches
  // customers who have no account and never will. It mounts no AppShell
  // and reads no store, so nothing on it depends on being signed in.
  //
  // The slug resolves SERVER-SIDE to a channel, which is what decides
  // the agent, the workflow and the organization. Nothing routable is in
  // this URL — it is a name for a line, not a key to one.
  {
    path: "/call/:slug",
    name: "public-call",
    component: () => import("./views/PublicCallView.vue"),
    meta: { public: true, title: "Support call" },
  },
  // W3 — the recordings a voice call plays. Deliberately on the
  // `workflows_edit` surface rather than a new one: an audio_key is a
  // field in a workflow step, so whoever may edit the graph may change
  // what it says. A new surface would mean a new row on Role Mapping
  // and a permission somebody has to grant before the feature works.
  {
    path: "/voice-prompts",
    name: "prompt-audio",
    component: () => import("./views/PromptAudioView.vue"),
    meta: {
      surface: "workflows_edit",
      title: "Voice prompts",
      subtitle: "The recordings a call plays — a recording replaces the step's text",
    },
  },
  // V0 — a test call into our own LiveKit room. `surface: "telephony"`
  // was already SUPER_ONLY in the server's table (it is the surface the
  // L1 telephony API lives on); what is new is that the surface now has a
  // page, which is why it is also in PAGE_SURFACES / PAGE_PATHS there.
  {
    path: "/telephony/test-call",
    name: "voice-test-call",
    component: () => import("./views/VoiceTestCallView.vue"),
    meta: {
      surface: "telephony",
      title: "Voice Test Call",
      subtitle: "Place a call into our own LiveKit room — no DID, no telephone",
    },
  },
  // The same idea for the OTHER engine: a conversation with a chat
  // workflow, with no WhatsApp and no channel. `surface: "chat_flow_test"`
  // is a surface of its own and NOT `chat` — /chat is a member talking to
  // an agent (EVERYONE), this drives a graph that calls tools and starts
  // workflow runs (ORG_AND_SUPER, like `telephony`).
  {
    path: "/chat-flows/test",
    name: "chat-flow-test",
    component: () => import("./views/ChatFlowTestView.vue"),
    meta: {
      surface: "chat_flow_test",
      title: "Chat Flow Test",
      subtitle: "Talk to a chat workflow — no WhatsApp, no channel, nothing written down",
    },
  },
  {
    path: "/chat",
    name: "chat",
    component: ChatView,
    meta: { surface: "chat", title: "Chat Console", subtitle: "Talk to agents visible to your department" },
  },
  // ── THE ONLY PAGE IN THIS APP THAT IS NOT PART OF THIS APP ──────────
  //
  // An external workshop technician opens this from a WhatsApp message
  // to fill ONE section of ONE complaint. They have no ELFA account and
  // never will (backend/scripts/seed_elfa_complaint_form.py, THE PART D
  // PROBLEM), so:
  //
  //   * `meta.public` EXEMPTS it from the guard below. That is the whole
  //     point: `beforeEach` would otherwise send an anonymous visitor to
  //     /login, which is a page they cannot pass. There is deliberately
  //     NO `meta.surface` either — `auth.canReach` is never consulted,
  //     and adding a surface here would only invite somebody to gate a
  //     page whose access control is the signed token in its URL.
  //   * the view renders NO app shell and touches NO store.
  //
  // The two path segments mirror the API's (/api/public/forms/{id}/{tok})
  // and are built by ONE function on the server —
  // `form_tokens.app_link_path` — so the link a human is sent and the
  // link the page calls cannot drift apart.
  //
  // The token is a bearer credential in a URL and therefore in browser
  // history and in every proxy log on the way; see the module docstring
  // of backend/app/services/form_tokens.py for what is and is not done
  // about that.
  {
    path: "/service-centre/:publicId/:token",
    name: "service-centre-section",
    component: () => import("./views/ServiceCentreSectionView.vue"),
    meta: { public: true },
  },
  // The documentation is STATIC content served from public/ (symlinks to
  // docs/site-user and docs/site). URLs with a file extension never reach
  // this router, but bare "/docs" has no extension, so Vite's SPA fallback
  // hands it to us — hard-redirect the browser to the real entry file
  // instead of letting the catch-all bounce it to the dashboard.
  {
    path: "/docs/:rest(.*)*",
    name: "docs-static",
    component: { render: () => null },
    meta: { public: true }, // docs are readable without signing in
    beforeEnter: () => {
      // Loop guard: if the SPA somehow receives the entry file's own URL
      // (misconfigured static serving), replacing to the same URL would
      // reload forever — bail out instead of looping.
      if (window.location.pathname !== "/docs/index.html") {
        window.location.replace("/docs/index.html");
      }
      return false;
    },
  },
  // ── The developer documentation — DEV BUILDS ONLY ─────────────────
  //
  // ``docs/site`` is written for people who have the repository: it names
  // internal endpoints, migration ids, table shapes and the reasoning
  // behind security decisions. It used to be symlinked into public/, and
  // Vite copies public/ wholesale into dist/, so every deployment shipped
  // it to whoever could reach the front door. The symlink is gone and a
  // dev-only Vite middleware serves it instead (see vite.config.ts).
  //
  // This route goes with it. Left in, a production bundle would still
  // announce ``/docs-dev/`` and bounce a visitor to a 404 that names a
  // site they cannot have — an advertisement for something deliberately
  // withheld. ``import.meta.env.DEV`` is resolved at BUILD time, so the
  // route is not merely inert in production, it is not in the bundle.
  ...(import.meta.env.DEV
    ? [
        {
          path: "/docs-dev/:rest(.*)*",
          name: "docs-dev-static",
          component: { render: () => null },
          meta: { public: true },
          beforeEnter: () => {
            if (window.location.pathname !== "/docs-dev/index.html") {
              window.location.replace("/docs-dev/index.html");
            }
            return false;
          },
        } as RouteRecordRaw,
      ]
    : []),
  // NOT `{ name: "dashboard" }` any more. /dashboard is a super_admin
  // surface under this policy, so a fixed redirect there would bounce a
  // member from an unknown URL to a page the guard immediately bounces
  // them off again — a loop, or at best a blank screen. The landing page
  // is the server's `home_path`.
  { path: "/:pathMatch(.*)*", redirect: () => useAuthStore().homePath },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// ── Auth + surface guard ───────────────────────────────────────────
//
// A typed URL for a surface out of reach lands on the caller's own home
// page instead of rendering a shell full of 403s. The API refuses the
// requests either way — see `surface_policy.install`.
router.beforeEach((to) => {
  const auth = useAuthStore();
  if (to.meta.public) {
    if (to.name === "login" && auth.isLoggedIn) return auth.homePath;
    return true;
  }
  if (!auth.isLoggedIn) {
    return { name: "login", query: { redirect: to.fullPath } };
  }
  const surface = to.meta.surface as string | undefined;
  if (!auth.canReach(surface)) return auth.homePath;
  return true;
});

export default router;
